'use client';

import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { FC, SyntheticEvent, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import { Gender, StoryLine } from '@/.gql/graphql';
import { Header } from '@/src/components/Header';
import { PackPreviewSeeAll } from '@/src/components/PackPreviewSeeAll';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  useMobileContext,
} from '@/src/providers';
import { getBlockName } from '@/src/utils/getBlockName';
import { getImageByGender } from '@/src/utils/getImageByGender';

const styles = {
  portal:
    '!h-dvh !max-h-none !min-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'flex gap-2 items-center mb-4',
  headWrap:
    'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  icon: 'w-5 h-5 fill-current',
  iconStyle: 'w-4 h-4 fill-current',
  title: 'text-base font-bold',
  name: 'text-base font-bold mb-3',
  wrapper:
    'grid grid-cols-2 row-auto gap-x-4 gap-y-3 justify-between bg-fill-menu pb-5',
  wrapperVideo: 'grid-cols-1',
  innerWrap: 'shrink-0 bg-fill-menu px-5 pt-16',
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg',
  preview: 'w-full h-[180px] object-cover object-top',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  info: 'absolute left-3 bottom-3',
  styleName: 'mb-[2px] text-sm font-medium text-left',
  styleCount: 'text-sm font-medium text-label-secondary text-left',
  closeButton: 'absolute top-7 z-2',
  closeIcon: 'w-6 h-6',
};

export const SeeAllStorylineItemsPortal: FC = () => {
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const {
    activeCategory,
    activeStyle,
    changeActiveStyle,
    genderMode,
    portalSeeAllMode,
    setPrevPortal,
    currentPack,
    prevPortal,
  } = useStyleContext();
  const { openPortal, closePortal } = usePortalContext();
  const t = useTranslations('MenuPage');
  const { isMobiles } = useMobileContext();

  const examples = useMemo(() => {
    return genderMode === Gender.Female
      ? (activeStyle as StoryLine)?.examplesWoman
      : (activeStyle as StoryLine)?.examplesMan;
  }, [activeStyle, genderMode]);

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');
    const currentIndex = event.currentTarget.getAttribute('data-style-ind');
    let position;

    if (styleId) {
      changeActiveStyle(styleId, portalSeeAllMode);

      if (currentIndex) {
        position = Number(currentIndex);
      }

      const currentStyle = currentPack?.find((item) => item.id === styleId);

      sendGTM({
        event: 'menu_selection',
        type: currentStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name: [
          'styles',
          'effectsCategories',
          'videoStyleCategories',
        ].includes(portalSeeAllMode)
          ? activeCategory?.name['en']
          : null,
        name: currentStyle?.name['en'],
        category_position: position,
        gender: genderMode,
      });
    }

    setPrevPortal('storyline');
    closePortal();
    openPortal(PORTALS.GenerateImage);
  };

  const handleClosePortal = () => {
    if (prevPortal === 'trending') {
      closePortal();
      openPortal(PORTALS.SeeAllTrending);
    } else {
      closePortal();
    }
  };

  return (
    <PortalModal
      portalId={PORTALS.SeeAllStorylines}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.headWrap, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.innerWrap}>
            <div className={styles.head}>
              <AiVideosIcon className={styles.icon} />
              <div className={styles.title}>
                {activeStyle?.name[profile?.lang]}
              </div>
            </div>
            <div className={twMerge(styles.wrapper, styles.wrapperVideo)}>
              {examples?.map((item, i) => (
                <PackPreviewSeeAll
                  key={item}
                  id={activeStyle.id}
                  index={i + 1}
                  isPopular={activeStyle.isPopular}
                  isNew={activeStyle.isNew}
                  image={item}
                  handleClickImage={handleClickImage}
                  isVideo={true}
                  isHorizontalVideo={true}
                  isSmall={false}
                  __typename={activeStyle.__typename}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
